import React, { useState, useEffect, useCallback } from 'react';
import './PastScansPage.css';
import { getReceiptHistory, saveReceiptAsShoppingList, unsaveReceipt } from '../utils/db';
import { auth } from '../firebase';

const RECEIPT_LIMIT = 10;

const STORE_NAMES = {
  store_tesco:      'Tesco',
  store_sainsburys: "Sainsbury's",
  store_asda:       'Asda',
  store_lidl:       'Lidl',
  store_aldi:       'Aldi',
  store_waitrose:   'Waitrose',
  store_morrisons:  'Morrisons',
  store_ms:         'M&S',
  store_iceland:    'Iceland',
  store_coop:       'Co-op',
};

const STORE_EMOJIS = {
  store_tesco:      '🔵',
  store_sainsburys: '🟠',
  store_asda:       '🟢',
  store_lidl:       '🔴',
  store_aldi:       '🔵',
  store_waitrose:   '⚫',
  store_morrisons:  '🟡',
  store_ms:         '⚫',
  store_iceland:    '🔵',
  store_coop:       '🟣',
};

function storeName(id)  { return STORE_NAMES[id]  || 'Unknown store'; }
function storeEmoji(id) { return STORE_EMOJIS[id] || '🏪'; }

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ─────────────────────────────────────────────────────────────────────────────

function PastScansPage({ onBack, onNavigate }) {
  const [view,            setView]            = useState('list');   // 'list' | 'detail'
  const [receipts,        setReceipts]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [selected,        setSelected]        = useState(null);     // receipt object
  const [saving,          setSaving]          = useState(false);
  const [savedToastId,    setSavedToastId]    = useState(null);     // receiptId just saved
  const [savedListId,     setSavedListId]     = useState(null);     // listId just created
  const [deletingId,      setDeletingId]      = useState(null);     // receiptId being deleted
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);     // receiptId awaiting confirm

  const isGuest = !auth.currentUser || auth.currentUser.isAnonymous;

  const load = useCallback(() => {
    setLoading(true);
    getReceiptHistory(RECEIPT_LIMIT + 2).then(data => {
      setReceipts(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isGuest) load();
    else setLoading(false);
  }, [isGuest, load]);

  // ── Back navigation ──────────────────────────────────────────────────────
  const handleBack = () => {
    if (view === 'detail') {
      setView('list');
      setSelected(null);
    } else {
      onBack();
    }
  };

  // ── Open receipt detail ──────────────────────────────────────────────────
  const openDetail = (receipt) => {
    setSelected(receipt);
    setView('detail');
    setSavedToastId(null);
  };

  // ── Save as list ─────────────────────────────────────────────────────────
  const handleSaveList = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const listId = await saveReceiptAsShoppingList({
        receiptId:       selected.id,
        matchedItems:    selected.matchedItems || [],
        detectedStoreId: selected.detectedStoreId,
      });
      // Update local state to reflect listCount increment
      const updated = receipts.map(r =>
        r.id === selected.id
          ? { ...r, listCount: (r.listCount || 0) + 1 }
          : r
      );
      setReceipts(updated);
      setSelected(prev => ({ ...prev, listCount: (prev.listCount || 0) + 1 }));
      setSavedToastId(selected.id);
      setSavedListId(listId);
    } catch (err) {
      alert('Could not save list. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Remove receipt from Past Scans (sets saved: false, keeps doc) ───────────
  const handleDeleteConfirm = async (id) => {
    setDeletingId(id);
    setConfirmDeleteId(null);
    const { success } = await unsaveReceipt(id);
    if (success) {
      setReceipts(prev => prev.filter(r => r.id !== id));
      if (selected?.id === id) {
        setView('list');
        setSelected(null);
      }
    }
    setDeletingId(null);
  };

  // receipts here are already filtered to saved: true by getReceiptHistory
  const savedCount = receipts.filter(r => r.status === 'complete').length;
  const atLimit    = savedCount >= RECEIPT_LIMIT;
  const nearLimit  = savedCount >= RECEIPT_LIMIT - 2 && !atLimit;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-page">

      {/* Header */}
      <div className="ps-header">
        <button className="ps-back" onClick={handleBack} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13,4 7,10 13,16"/>
          </svg>
        </button>
        <span className="ps-header-title">
          {view === 'detail' && selected
            ? `${storeName(selected.detectedStoreId)} · ${formatDate(selected.processedAt)}`
            : 'Past scans'}
        </span>
        <div className="ps-header-spacer" />
      </div>

      {/* ── List view ── */}
      {view === 'list' && (
        <div className="ps-body">

          {/* Guest state */}
          {isGuest && (
            <div className="ps-guest">
              <div className="ps-guest-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="12" height="16" rx="2"/>
                  <line x1="7" y1="7" x2="13" y2="7"/>
                  <line x1="7" y1="10" x2="13" y2="10"/>
                  <line x1="7" y1="13" x2="10" y2="13"/>
                </svg>
              </div>
              <p className="ps-guest-heading">Sign in to save your scan history</p>
              <p className="ps-guest-sub">Once signed in, your scans are saved so you can reuse them to build shopping lists for future shops.</p>
            </div>
          )}

          {/* Loading */}
          {!isGuest && loading && (
            <div className="ps-loading">
              <div className="ps-spinner" />
            </div>
          )}

          {/* Empty */}
          {!isGuest && !loading && receipts.length === 0 && (
            <div className="ps-empty">
              <div className="ps-empty-icon" aria-hidden="true">
                <svg width="36" height="36" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="12" height="16" rx="2"/>
                  <line x1="7" y1="7" x2="13" y2="7"/>
                  <line x1="7" y1="10" x2="13" y2="10"/>
                  <line x1="7" y1="13" x2="10" y2="13"/>
                </svg>
              </div>
              <p className="ps-empty-heading">No scans yet</p>
              <p className="ps-empty-sub">Scan a receipt and tap "Save to Past scans" to keep it here. Saved receipts can be reused to build shopping lists.</p>
            </div>
          )}

          {/* Receipt list */}
          {!isGuest && !loading && receipts.length > 0 && (
            <>
              {/* Saved slot notices */}
              {atLimit && (
                <div className="ps-limit-banner ps-limit-banner--full">
                  <span>Past scans is full ({RECEIPT_LIMIT} saved).</span>
                  <span>Remove an older scan to free up a saved slot.</span>
                </div>
              )}
              {nearLimit && (
                <div className="ps-limit-banner ps-limit-banner--near">
                  {RECEIPT_LIMIT - savedCount} saved slot{RECEIPT_LIMIT - savedCount !== 1 ? 's' : ''} remaining.
                </div>
              )}

              <ul className="ps-list">
                {receipts.map(r => {
                  const unmatched = (r.itemCount || 0) - (r.matchedCount || 0);
                  const failed    = r.status === 'failed';
                  const listCount = r.listCount || 0;
                  const isDeleting = deletingId === r.id;
                  const confirmingDelete = confirmDeleteId === r.id;

                  return (
                    <li key={r.id} className={`ps-card${failed ? ' ps-card--failed' : ''}`}>
                      <button className="ps-card-main" onClick={() => !failed && openDetail(r)} disabled={failed}>
                        <div className="ps-card-store">
                          <span className="ps-card-emoji" aria-hidden="true">{storeEmoji(r.detectedStoreId)}</span>
                          <div className="ps-card-info">
                            <span className="ps-card-name">
                              {failed ? 'Scan failed' : storeName(r.detectedStoreId)}
                            </span>
                            <span className="ps-card-date">
                              {formatDate(r.processedAt)} · {formatTime(r.processedAt)}
                            </span>
                          </div>
                        </div>
                        <div className="ps-card-meta">
                          {failed ? (
                            <span className="ps-badge ps-badge--fail">Failed</span>
                          ) : (
                            <>
                              <span className="ps-badge ps-badge--ok">{r.matchedCount || 0} matched</span>
                              {unmatched > 0 && (
                                <span className="ps-badge ps-badge--warn">{unmatched} unmatched</span>
                              )}
                              {listCount > 0 && (
                                <span className="ps-badge ps-badge--used">
                                  Used {listCount}×
                                </span>
                              )}
                            </>
                          )}
                          {!failed && (
                            <svg className="ps-card-chevron" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="7,4 13,10 7,16"/>
                            </svg>
                          )}
                        </div>
                      </button>

                      {/* Delete controls */}
                      <div className="ps-card-delete-row">
                        {confirmingDelete ? (
                          <>
                            <span className="ps-delete-confirm-text">Remove from Past scans?</span>
                            <button
                              className="ps-delete-confirm-yes"
                              onClick={() => handleDeleteConfirm(r.id)}
                              disabled={isDeleting}
                            >
                              {isDeleting ? 'Removing…' : 'Remove'}
                            </button>
                            <button
                              className="ps-delete-confirm-no"
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="ps-delete-btn"
                            onClick={() => setConfirmDeleteId(r.id)}
                            aria-label={`Remove scan from ${storeName(r.detectedStoreId)}`}
                          >
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="3,6 17,6"/>
                              <path d="M8 6V4h4v2"/>
                              <rect x="5" y="6" width="10" height="12" rx="1"/>
                              <line x1="8" y1="10" x2="8" y2="14"/>
                              <line x1="12" y1="10" x2="12" y2="14"/>
                            </svg>
                            Remove
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <p className="ps-count-note">
                {savedCount} of {RECEIPT_LIMIT} saved receipts
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Detail view ── */}
      {view === 'detail' && selected && (() => {
        const unmatched = (selected.itemCount || 0) - (selected.matchedCount || 0);
        const listCount = selected.listCount || 0;
        const justSaved = savedToastId === selected.id;

        return (
          <div className="ps-body ps-detail">

            {/* Summary */}
            <div className="ps-detail-summary">
              <div className="ps-detail-store">
                <span className="ps-detail-emoji" aria-hidden="true">{storeEmoji(selected.detectedStoreId)}</span>
                <div>
                  <p className="ps-detail-store-name">{storeName(selected.detectedStoreId)}</p>
                  <p className="ps-detail-date">{formatDate(selected.processedAt)} · {formatTime(selected.processedAt)}</p>
                </div>
              </div>
              <div className="ps-detail-stats">
                <span>{selected.itemCount || 0} items found</span>
                <span className="ps-dot">·</span>
                <span className="ps-stat-ok">{selected.matchedCount || 0} matched</span>
                {unmatched > 0 && (
                  <>
                    <span className="ps-dot">·</span>
                    <span className="ps-stat-warn">{unmatched} not in catalogue</span>
                  </>
                )}
              </div>
              {listCount > 0 && (
                <p className="ps-detail-used-note">
                  Previously used to create {listCount} shopping list{listCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Matched items */}
            {selected.matchedItems?.length > 0 && (
              <div className="ps-detail-section">
                <p className="ps-detail-label">Matched items</p>
                <ul className="ps-item-list">
                  {selected.matchedItems.map((item, i) => (
                    <li key={i} className="ps-item ps-item--matched">
                      <span className="ps-item-name">{item.matchedProductName || item.productText}</span>
                      {item.pricePence != null && (
                        <span className="ps-item-price">£{(item.pricePence / 100).toFixed(2)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Unmatched items */}
            {selected.unmatchedItems?.length > 0 && (
              <div className="ps-detail-section">
                <p className="ps-detail-label ps-detail-label--grey">Not in our catalogue</p>
                <ul className="ps-item-list">
                  {selected.unmatchedItems.map((item, i) => (
                    <li key={i} className="ps-item ps-item--unmatched">
                      <span className="ps-item-name">{item.productText || item.rawText}</span>
                      {item.pricePence != null && (
                        <span className="ps-item-price">£{(item.pricePence / 100).toFixed(2)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action */}
            <div className="ps-detail-actions">
              {justSaved ? (
                <div className="ps-saved-confirm-wrap">
                  <div className="ps-saved-confirm">
                    <span className="ps-saved-tick">✓</span>
                    Shopping list created from your {storeName(selected.detectedStoreId)} shop
                  </div>
                  {onNavigate && (
                    <button
                      className="ps-view-list-btn"
                      onClick={() => onNavigate('basket')}
                    >
                      View list
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <button
                    className="ps-save-btn"
                    onClick={handleSaveList}
                    disabled={saving || !selected.matchedItems?.length}
                  >
                    {saving ? 'Creating list…' : listCount > 0 ? 'Create another list' : 'Create shopping list'}
                  </button>
                  {listCount > 0 && (
                    <p className="ps-action-note">
                      Last used {listCount === 1 ? 'once' : `${listCount} times`} to create a list
                    </p>
                  )}
                </>
              )}
            </div>

          </div>
        );
      })()}

    </div>
  );
}

export default PastScansPage;
